import {useCallback, useMemo} from 'react';
import {useUserTokenOrSessionId} from '../userStore/user.selector';
import {useWishlist} from './wishlist.hooks';
import {WishlistItem} from '@aboutyou/backbone/endpoints/wishlist/getWishlist';
import {BapiProduct} from '@aboutyou/backbone';
import {CentAmount} from '@aboutyou/backbone/types/BapiProduct';
// eslint-disable-next-line monorepo/no-internal-import
import {useCampaignKey} from '@aboutyou/logic-components/src/backend-services/Campaigns/campaign-state/campaign.hooks';
import {reduceVariantIdsToProductIds} from '../../utils/reduceVariantIdsToProductIds';

export const useWishlistSession = () => {
  const {data: session, isLoading} = useUserTokenOrSessionId();
  const campaignKey = useCampaignKey();

  const cacheKey = useMemo(() => {
    return `fetch-wishlist-${session?.resolvedId || ''}${campaignKey || ''}`;
  }, [campaignKey, session?.resolvedId]);

  return {session, cacheKey, campaignKey, isLoading};
};

export const useWishlistCount = () => {
  const {data, isLoading} = useWishlist();
  const isReallyLoading = isLoading || !data?.productIds.length;
  return {count: data?.productIds.length ?? 0, isLoading: isReallyLoading};
};

type ExtendedWishlistItemType = WishlistItem & {
  product?: BapiProduct & {
    price?: {
      campaignReduction?: {
        priceBeforeReductionWithTax: CentAmount;
        discountPercentage: number;
        reductionAmountWithTax?: CentAmount;
      };
    };
  };
  status?: string;
};

export const useWishlistContainsCampaignItems = () => {
  const {data: wishlist} = useWishlist();

  return wishlist?.items
    ?.filter((item: ExtendedWishlistItemType) => item.status !== 'unavailable')
    .some(
      (wishlistItem: ExtendedWishlistItemType) =>
        wishlistItem?.product?.price?.campaignReduction ||
        wishlistItem?.product?.priceRange?.min.appliedReductions.find(
          reduction => reduction.category === 'campaign',
        ),
    );
};

export const useIsProductInWishlist = (productId?: number) => {
  const {data} = useWishlist();
  return !!(productId && data?.productIds?.includes(productId)) || false;
};

export const useGetProductIsInWishlist = () => {
  const {data: wishlist} = useWishlist();

  return useCallback(
    ({productId: productIdToCompare}: {productId: number}) => {
      const productIds = wishlist?.productIds ?? [];
      return productIds.includes(productIdToCompare);
    },
    [wishlist?.productIds],
  );
};

export const useGetWishlistItemForProduct = () => {
  const {data: wishlist} = useWishlist();

  return useCallback(
    ({productId: productIdToCompare}: {productId: number}) => {
      return wishlist?.items.find(
        item => item.product?.id === productIdToCompare,
      );
    },
    [wishlist],
  );
};

export const useGlobalWishlistItemsToken = (
  globalModifier?: string | undefined,
): string | null => {
  const {data, isLoading} = useWishlist();
  if (!data || isLoading) {
    return null;
  }
  // if key is null, it means the wishlist is still loading
  if (!data.key) {
    return null;
  }
  return (
    data.items
      .map(item =>
        [item.key, item.product?.isSoldOut || !item.product?.isActive].join(
          '|',
        ),
      )
      .join(';') + globalModifier
  );
};

export const useIsWishlistLoaded = () => {
  const {data} = useWishlist();
  return Boolean(data);
};

export const useWishlistProductIdsWithSizeIds = () => {
  const {data: wishlist} = useWishlist();
  return reduceVariantIdsToProductIds(wishlist?.items ?? []);
};
